import React, { useEffect, useRef, useState } from "react";
import { Newspaper, PenLine, Trash2, Image as ImageIcon, X, Eye, EyeOff } from "lucide-react";
import { api } from "../../lib/api";
import { D, JAKARTA, inputStyle, Card, SectionTitle, Btn, Chip, Field, Msg, fmtTime } from "./ui";
import { IconBtn } from "./RadioPanel";

const EMPTY = { title: "", body: "", city_id: "", cover_url: "", cover_media_id: null, status: "draft" };

// Panel Koran: artikel Redaksi Jawsel (seluruh wilayah) & rubrik per kota (ditulis walikota atau admin)
export function KoranPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [form, setForm] = useState(null);   // null = tertutup; {...} = editor terbuka
  const [edit, setEdit] = useState(null);
  const [msg, setMsg] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.adminKoran(token).then(d => setRows(d.articles || [])).catch(e => setMsg(e.message));
  useEffect(() => { load(); api.listMayors(token).then(d => setCitiesList(d.mayors || [])).catch(() => {}); }, []); // eslint-disable-line

  const cityName = id => (id ? (citiesList.find(c => c.city_id === id)?.city_name || id) : "Jawsel");

  async function submit(e) {
    e.preventDefault();
    setMsg("");
    const payload = { ...form, city_id: form.city_id || null, cover_url: form.cover_url || null };
    try {
      if (edit) await api.patchKoran(token, edit, payload); else await api.createKoran(token, payload);
      setForm(null); setEdit(null); setMsg(edit ? "Artikel diperbarui" : "Artikel dibuat"); load();
    } catch (err) { setMsg(err.message); }
  }
  async function toggleStatus(a) {
    await api.patchKoran(token, a.id, { status: a.status === "published" ? "draft" : "published" }).catch(e => setMsg(e.message));
    load();
  }
  async function del(a) {
    if (!window.confirm(`Hapus artikel "${a.title}"?`)) return;
    await api.deleteKoran(token, a.id).catch(e => setMsg(e.message));
    load();
  }

  const shown = rows.filter(a => filter === "all" || (filter === "published" ? a.status === "published" : filter === "draft" ? a.status === "draft" : a.author?.role === "mayor"));

  return (
    <div style={{ padding: "22px 20px 30px", fontFamily: JAKARTA }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 27, fontWeight: 800, letterSpacing: -0.6, color: D.ink }}>Koran Jawsel</h2>
        <Chip tone="blue">{rows.filter(a => a.status === "published").length} terbit</Chip>
        <Chip tone="gray">{rows.filter(a => a.status === "draft").length} draf</Chip>
        <Msg text={msg} testid="koran-msg" />
        <div style={{ flex: 1 }} />
        <Btn testid="koran-new" onClick={() => { setEdit(null); setForm({ ...EMPTY }); }}><PenLine size={15} /> Tulis Artikel</Btn>
      </div>

      {form && (
        <Card testid="koran-editor" style={{ marginBottom: 18 }}>
          <SectionTitle title={edit ? "Sunting Artikel" : "Artikel Baru"} right={<IconBtn testid="koran-editor-close" onClick={() => { setForm(null); setEdit(null); }}><X size={14} /></IconBtn>} />
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Field label="Judul" grow><input data-testid="koran-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Judul berita" style={inputStyle} required /></Field>
              <Field label="Rubrik">
                <select data-testid="koran-city" value={form.city_id} onChange={e => setForm({ ...form, city_id: e.target.value })} style={{ ...inputStyle, width: 200 }}>
                  <option value="">Koran Jawsel (semua kota)</option>
                  {citiesList.map(c => <option key={c.city_id} value={c.city_id}>Kota {c.city_name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select data-testid="koran-status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={{ ...inputStyle, width: 140 }}>
                  <option value="draft">Draf</option>
                  <option value="published">Terbit</option>
                </select>
              </Field>
            </div>
            <CoverPicker token={token} form={form} setForm={setForm} setMsg={setMsg} />
            <Field label="Isi berita"><textarea data-testid="koran-body" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={8} placeholder="Tulis isi berita..." style={{ ...inputStyle, resize: "vertical", lineHeight: 1.55 }} required /></Field>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn type="submit" testid="koran-submit"><Newspaper size={15} /> {edit ? "Simpan" : form.status === "published" ? "Terbitkan" : "Simpan Draf"}</Btn>
              <Btn variant="ghost" testid="koran-cancel" onClick={() => { setForm(null); setEdit(null); }}>Batal</Btn>
            </div>
          </form>
        </Card>
      )}

      <Card>
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {[["all", "Semua"], ["published", "Terbit"], ["draft", "Draf"], ["mayor", "Tulisan Walikota"]].map(([k, l]) => (
            <button key={k} data-testid={`koran-filter-${k}`} onClick={() => setFilter(k)}
              style={{ padding: "7px 14px", borderRadius: 99, border: 0, background: filter === k ? D.dark : D.cream, color: filter === k ? "#fff" : D.ink, fontSize: 12, fontWeight: 700, fontFamily: JAKARTA, cursor: "pointer" }}>{l}</button>
          ))}
        </div>
        <table data-testid="koran-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: D.sub, fontSize: 11 }}>
              {["Artikel", "Rubrik", "Penulis", "Status", "Waktu", "Aksi"].map(h => <th key={h} style={{ padding: "6px 8px", fontWeight: 700 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map(a => (
              <tr key={a.id} data-testid={`koran-row-${a.id}`} style={{ borderTop: `1px solid ${D.line}` }}>
                <td style={{ padding: "10px 8px", maxWidth: 380 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 44, height: 32, borderRadius: 8, background: D.cream, overflow: "hidden", flex: "none", display: "grid", placeItems: "center" }}>
                      {a.cover ? <img src={api.mediaUrl(a.cover)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ImageIcon size={14} color={D.gray} />}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <b style={{ display: "block", color: D.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</b>
                      <span style={{ fontSize: 12, color: D.sub, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.body?.slice(0, 90)}</span>
                    </div>
                  </div>
                </td>
                <td style={{ padding: "10px 8px", color: D.sub }}>{cityName(a.city_id)}</td>
                <td style={{ padding: "10px 8px", color: D.sub }}>{a.author?.nama}</td>
                <td style={{ padding: "10px 8px" }}><Chip tone={a.status === "published" ? "green" : "gray"} testid={`koran-status-${a.id}`}>{a.status === "published" ? "Terbit" : "Draf"}</Chip></td>
                <td style={{ padding: "10px 8px", color: D.sub, whiteSpace: "nowrap" }}>{fmtTime(a.published_at || a.updated_at)}</td>
                <td style={{ padding: "10px 8px" }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <IconBtn testid={`koran-toggle-${a.id}`} title={a.status === "published" ? "Tarik ke draf" : "Terbitkan"} onClick={() => toggleStatus(a)}>{a.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />}</IconBtn>
                    <IconBtn testid={`koran-edit-${a.id}`} onClick={() => { setEdit(a.id); setForm({ title: a.title, body: a.body, city_id: a.city_id || "", cover_url: a.cover_url || "", cover_media_id: a.cover_media_id || null, status: a.status }); window.scrollTo({ top: 0, behavior: "smooth" }); }}><PenLine size={14} /></IconBtn>
                    <IconBtn testid={`koran-delete-${a.id}`} danger onClick={() => del(a)}><Trash2 size={14} /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {shown.length === 0 && <p data-testid="koran-empty" style={{ color: D.sub, fontSize: 13, margin: "12px 8px 4px" }}>Belum ada artikel.</p>}
      </Card>
    </div>
  );
}

// Sampul: unggah gambar (object storage) atau tempel URL
function CoverPicker({ token, form, setForm, setMsg }) {
  const ref = useRef(null);
  const [prog, setProg] = useState(null);
  async function pick(f) {
    if (!f) return;
    try {
      setProg(0);
      const m = await api.uploadMedia(token, f, "image", setProg);
      setForm(s => ({ ...s, cover_media_id: m.id, cover_url: "" }));
    } catch (err) { setMsg(err.message); } finally { setProg(null); }
  }
  const src = form.cover_media_id ? api.mediaUrl(form.cover_media_id) : (form.cover_url ? api.mediaUrl(form.cover_url) : null);
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ width: 120, height: 76, borderRadius: 14, background: D.cream, overflow: "hidden", display: "grid", placeItems: "center", border: `1.5px solid ${D.line}` }}>
        {src ? <img data-testid="koran-cover-preview" src={src} alt="sampul" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <ImageIcon size={20} color={D.gray} />}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 240 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Btn variant="soft" small testid="koran-cover-upload" onClick={() => ref.current?.click()} disabled={prog != null}><ImageIcon size={13} /> {prog != null ? `Mengunggah ${Math.round(prog * 100)}%` : "Unggah sampul"}</Btn>
          {(form.cover_media_id || form.cover_url) && <Btn variant="ghost" small testid="koran-cover-clear" onClick={() => setForm({ ...form, cover_media_id: null, cover_url: "" })}><X size={13} /> Hapus sampul</Btn>}
          <input ref={ref} data-testid="koran-cover-file" type="file" accept="image/*" style={{ display: "none" }} onChange={e => pick(e.target.files?.[0])} />
        </div>
        <input data-testid="koran-cover-url" value={form.cover_url} onChange={e => setForm({ ...form, cover_url: e.target.value, cover_media_id: null })} placeholder="atau tempel URL gambar (https://...)" style={inputStyle} />
      </div>
    </div>
  );
}
